import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Stack } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { projectService } from "../api/services/projectService";

/**
 * Quick "create a project" flow used from the Tickets sidebar's + button and
 * the empty-state when no projects exist yet. The full ProjectsPage (dates,
 * members, completion tracking) is still the place for everything beyond a
 * name -- this just gets a project to select so ticket views have something
 * to scope to.
 */
export default function CreateProjectDialog({ open, onClose, onCreated }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState("");

  const createMutation = useMutation({
    mutationFn: () => projectService.create({ name, member_employee_ids: [] }),
    onSuccess: (project) => {
      enqueueSnackbar("Project created", { variant: "success" });
      setName("");
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      onCreated && onCreated(project);
      onClose();
    },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Failed to create project", { variant: "error" }),
  });

  const handleClose = () => {
    setName("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>New Project</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Project name" value={name} onChange={(e) => setName(e.target.value)}
            fullWidth autoFocus required
            onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) createMutation.mutate(); }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} color="inherit">Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || createMutation.isPending} onClick={() => createMutation.mutate()}>
          {createMutation.isPending ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
